import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LoginInput, RegisterInput, UserResponse } from '@clair-avenir/shared';
import { http } from '@/lib/http';
import { useApiMutation, type UseApiMutationOptions } from '@/lib/use-api-mutation';

export const sessionQueryKey = ['auth', 'session'] as const;

export const sessionQueryOptions = () =>
  queryOptions({
    queryKey: sessionQueryKey,
    queryFn: () => http<UserResponse>('/users/me'),
    retry: false,
    staleTime: 60_000,
  });

/** Sessão atual (null se não autenticado — ver `_authenticated.tsx` pro guard de rota). */
export function useSession() {
  return useQuery(sessionQueryOptions());
}

export function useRegister(opts: UseApiMutationOptions<UserResponse, RegisterInput> = {}) {
  const queryClient = useQueryClient();
  return useApiMutation<UserResponse, RegisterInput>({
    mutationFn: (input) => http<UserResponse>('/auth/register', { method: 'POST', json: input, skipAuthRefresh: true }),
    ...opts,
    onSuccess: (user, vars, onMutateResult, ctx) => {
      queryClient.setQueryData(sessionQueryKey, user);
      opts.onSuccess?.(user, vars, onMutateResult, ctx);
    },
  });
}

export function useLogin(opts: UseApiMutationOptions<UserResponse, LoginInput> = {}) {
  const queryClient = useQueryClient();
  return useApiMutation<UserResponse, LoginInput>({
    mutationFn: (input) => http<UserResponse>('/auth/login', { method: 'POST', json: input, skipAuthRefresh: true }),
    ...opts,
    onSuccess: (user, vars, onMutateResult, ctx) => {
      queryClient.setQueryData(sessionQueryKey, user);
      opts.onSuccess?.(user, vars, onMutateResult, ctx);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    // Sem skipAuthRefresh: se o access expirou mas o refresh ainda vale, o próprio
    // http() renova a sessão e reenvia o logout — evita o botão "Sair" ficar preso
    // num 401 silencioso depois de ~15 min de inatividade.
    mutationFn: () => http<void>('/auth/logout', { method: 'POST' }),
    onSettled: () => {
      // Mesmo se o logout falhar (ex.: refresh também expirado), a sessão local é
      // tratada como encerrada — não há como continuar autenticado sem tokens válidos.
      queryClient.setQueryData(sessionQueryKey, null);
      queryClient.clear();
    },
  });
}
